/**
 * HookLibrary — reusable email/LinkedIn/content hook library for BDRs to pull
 * into outreach cadences. Three columns by kind, with search + filter pills,
 * a "+ New hook" CTA, and a "Pull into cadence" mini-modal.
 */
import { useEffect, useState } from "react";
import {
  loadLibrary,
  saveLibrary,
  incrementUsage,
  type LibraryHook,
  type HookKind,
  type HookStatus,
} from "@/lib/marketing-hook-library";

const BLACK = "#0B0B0F";
const RED  = "#DC2626";

type Filter = "all" | "new" | "trending" | "approved" | "draft";

const KIND_LABEL: Record<HookKind, string> = {
  email:    "Email hooks",
  linkedin: "LinkedIn hooks",
  content:  "Content hooks",
};

const STATUS_STYLE: Record<HookStatus, { bg: string; fg: string; label: string }> = {
  approved:  { bg: "rgba(22,163,74,0.10)",  fg: "#16A34A", label: "Approved" },
  trending:  { bg: "rgba(220,38,38,0.10)",  fg: RED,        label: "Trending" },
  new:       { bg: "rgba(15,23,42,0.08)",   fg: BLACK,       label: "New" },
  draft:     { bg: "rgba(148,163,184,0.10)", fg: "#6B6B70", label: "Draft" },
};

const CADENCES = [
  "Profit+ Industrial CFO outreach",
  "Industrial-CFO finance angle",
  "Automotive-purchasing intro",
  "Tier-1 supplier nurture",
  "Aerospace mfg-ops re-engagement",
];

export default function HookLibrary() {
  const [hooks, setHooks] = useState<LibraryHook[]>([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [pullTarget, setPullTarget] = useState<LibraryHook | null>(null);
  const [newHookOpen, setNewHookOpen] = useState(false);

  useEffect(() => {
    setHooks(loadLibrary());
  }, []);

  function applyFilters(h: LibraryHook): boolean {
    if (filter !== "all" && h.status !== filter) return false;
    if (search.trim()) {
      const s = search.toLowerCase();
      if (!h.title.toLowerCase().includes(s) &&
          !h.snippet.toLowerCase().includes(s) &&
          !h.tags.some((t) => t.toLowerCase().includes(s))) return false;
    }
    return true;
  }

  const visible = hooks.filter(applyFilters);
  const byKind: Record<HookKind, LibraryHook[]> = {
    email:    visible.filter((h) => h.kind === "email"),
    linkedin: visible.filter((h) => h.kind === "linkedin"),
    content:  visible.filter((h) => h.kind === "content"),
  };

  function handlePullConfirm(cadence: string, step: number) {
    if (!pullTarget) return;
    setHooks(incrementUsage(pullTarget.id));
    try {
      const auditKey = "aip:marketing-audit:v1";
      const raw = localStorage.getItem(auditKey);
      const log = raw ? JSON.parse(raw) : [];
      log.push({
        ts: new Date().toISOString(),
        action: "hook_pulled",
        hook_id: pullTarget.id,
        hook_title: pullTarget.title,
        cadence,
        step,
      });
      localStorage.setItem(auditKey, JSON.stringify(log));
    } catch { /* ignore */ }
    setPullTarget(null);
  }

  function handleNewHook(form: { title: string; snippet: string; kind: HookKind; tags: string }) {
    const now = new Date().toISOString();
    const next: LibraryHook = {
      id: `h-${form.kind.slice(0,2)}-${Date.now()}`,
      kind: form.kind,
      title: form.title.trim(),
      snippet: form.snippet.trim(),
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      status: "draft",
      usage_count: 0,
      created_at: now,
      updated_at: now,
    };
    const updated = [...hooks, next];
    setHooks(updated);
    saveLibrary(updated);
    setNewHookOpen(false);
  }

  return (
    <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-neutral-100 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[9px] uppercase tracking-[0.22em] text-neutral-500 mb-0.5">Reusable library</div>
          <h2 className="text-base font-bold" style={{ color: BLACK }}>
            BDR Hook Library
            <span className="ml-2 text-xs font-semibold text-neutral-500">{hooks.length} hooks</span>
          </h2>
        </div>
        <button
          onClick={() => setNewHookOpen(true)}
          className="px-4 py-2 rounded-lg text-xs font-bold text-white"
          style={{ background: RED }}
        >
          + New hook
        </button>
      </div>

      {/* Search + filter pills */}
      <div className="px-5 py-3 border-b border-neutral-100 flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search hooks by title, snippet, or tag..."
          className="flex-1 min-w-[220px] border border-neutral-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-[#0B0B0F]"
        />
        <div className="flex gap-1.5">
          {(["all","new","trending","approved","draft"] as const).map((f) => {
            const a = filter === f;
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className="text-[10px] font-bold uppercase tracking-[0.1em] px-2.5 py-1 rounded-full border capitalize"
                style={{
                  background: a ? BLACK : "white",
                  color: a ? "white" : "#525258",
                  borderColor: a ? BLACK : "#E5E5E7",
                }}
              >
                {f}
              </button>
            );
          })}
        </div>
      </div>

      {/* 3-column grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 divide-y lg:divide-y-0 lg:divide-x divide-neutral-100">
        {(["email","linkedin","content"] as const).map((kind) => (
          <div key={kind} className="p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-bold mb-3">
              {KIND_LABEL[kind]} <span className="text-neutral-300">· {byKind[kind].length}</span>
            </div>
            <div className="space-y-3">
              {byKind[kind].length === 0 ? (
                <div className="text-xs text-neutral-300 py-6 text-center">No hooks match this filter.</div>
              ) : byKind[kind].map((h) => {
                const ss = STATUS_STYLE[h.status];
                return (
                  <div key={h.id} className="rounded-lg border border-neutral-200 p-3 hover:border-neutral-300 transition-colors">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="text-[12px] font-bold text-neutral-800 leading-snug flex-1 min-w-0">{h.title}</div>
                      <span
                        className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded flex-shrink-0"
                        style={{ background: ss.bg, color: ss.fg }}
                      >
                        {ss.label}
                      </span>
                    </div>
                    <div className="text-[11px] text-neutral-600 leading-relaxed mb-2 line-clamp-3">
                      {h.snippet}
                    </div>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {h.tags.slice(0, 4).map((t) => (
                        <span
                          key={t}
                          className="text-[9px] text-neutral-500 bg-neutral-50 border border-neutral-200 px-1.5 py-0.5 rounded"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-neutral-500">{h.usage_count} pulls</span>
                      <button
                        onClick={() => setPullTarget(h)}
                        className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded text-white"
                        style={{ background: RED }}
                      >
                        Pull into cadence
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {pullTarget && (
        <PullModal
          hook={pullTarget}
          onClose={() => setPullTarget(null)}
          onConfirm={handlePullConfirm}
        />
      )}
      {newHookOpen && (
        <NewHookModal
          onClose={() => setNewHookOpen(false)}
          onSave={handleNewHook}
        />
      )}
    </div>
  );
}

function PullModal({
  hook,
  onClose,
  onConfirm,
}: {
  hook: LibraryHook;
  onClose: () => void;
  onConfirm: (cadence: string, step: number) => void;
}) {
  const [cadence, setCadence] = useState(CADENCES[0]);
  const [step, setStep] = useState(2);

  useEffect(() => {
    function esc(e: KeyboardEvent) { if (e.key === "Escape") onClose(); }
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="px-5 py-4 flex items-center justify-between" style={{ background: BLACK }}>
          <h3 className="text-white font-bold text-base">Pull hook into cadence</h3>
          <button onClick={onClose} aria-label="Close" className="text-neutral-300 hover:text-white text-xl">×</button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-0.5">Hook</div>
            <div className="text-sm font-semibold text-neutral-800">{hook.title}</div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-1">Cadence</label>
            <select
              value={cadence}
              onChange={(e) => setCadence(e.target.value)}
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#0B0B0F]"
            >
              {CADENCES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-1">Drop into step</label>
            <input
              type="number"
              min={1}
              max={10}
              value={step}
              onChange={(e) => setStep(Number(e.target.value) || 1)}
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#0B0B0F]"
            />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-neutral-100 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-neutral-600 border border-neutral-200 hover:bg-neutral-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(cadence, step)}
            className="px-5 py-2 rounded-lg text-sm font-bold text-white"
            style={{ background: RED }}
          >
            Add to cadence
          </button>
        </div>
      </div>
    </div>
  );
}

function NewHookModal({
  onClose,
  onSave,
}: {
  onClose: () => void;
  onSave: (form: { title: string; snippet: string; kind: HookKind; tags: string }) => void;
}) {
  const [form, setForm] = useState({ title: "", snippet: "", kind: "email" as HookKind, tags: "" });

  useEffect(() => {
    function esc(e: KeyboardEvent) { if (e.key === "Escape") onClose(); }
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="px-5 py-4 flex items-center justify-between" style={{ background: BLACK }}>
          <h3 className="text-white font-bold text-base">New hook</h3>
          <button onClick={onClose} aria-label="Close" className="text-neutral-300 hover:text-white text-xl">×</button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-1">Kind</label>
            <select
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value as HookKind })}
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
            >
              <option value="email">Email</option>
              <option value="linkedin">LinkedIn</option>
              <option value="content">Content</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-1">Title</label>
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Profit+ pilot saved $X in 90 days"
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-1">Snippet</label>
            <textarea
              rows={4}
              value={form.snippet}
              onChange={(e) => setForm({ ...form, snippet: e.target.value })}
              placeholder="2–3 sentence preview..."
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm resize-none"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-1">Tags (comma-separated)</label>
            <input
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
              placeholder="industrial, CFO, stage:awareness"
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-neutral-100 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-neutral-600 border border-neutral-200 hover:bg-neutral-50"
          >
            Cancel
          </button>
          <button
            onClick={() => form.title && form.snippet && onSave(form)}
            className="px-5 py-2 rounded-lg text-sm font-bold text-white"
            style={{ background: RED }}
          >
            Save as draft
          </button>
        </div>
      </div>
    </div>
  );
}
